import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Department,
} from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Button from '@lib/components/Button/Button';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import Trans from '@lib/components/Trans/Trans';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import { IconName } from '@lib/components/Modal/enums';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useDepartmentsQueryHook from 'utils/fetch/hooks/useDepartmentsQueryHook';
import getDepartmentsTableActionsMenu from './utils/getDepartmentsTableActionsMenu';
import getDepartmentsTableColumns from './utils/getDepartmentsTableColumns';
import useDepartmentsListActions from './utils/useDepartmentsListActions';
import useDepartmentsTableTree from './utils/useDepartmentsTableTree';

const modals = [Modals.DepartmentDestroyModal];

export default function DepartmentsContainer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DepartmentsManagement) ===
    AccessLevelEnum.Read;

  const departmentsQueryHookResult = useDepartmentsQueryHook({
    searchQuery,
    paginationParams,
    orderingParams: paginationParams,
  });

  const useBulkSelectResult = useTableItemsBulkSelect<Department>();
  const useModalManagementResult = useModalManagement(modals, () =>
    useBulkSelectResult.handleSelectSingleTableItem(null),
  );
  const { closeModal, isModalOpened } = useModalManagementResult;
  const departmentsTableTree = useDepartmentsTableTree({
    collection: departmentsQueryHookResult.collection,
    searchQuery,
  });
  const {
    destroyLoading,
    onConfirmedDelete,
    onCreate,
    onDelete,
    onEdit,
    onView,
  } = useDepartmentsListActions({
    departmentsQueryHookResult,
    onDestroyed: departmentsTableTree.evictNode,
    useModalManagementResult,
    useBulkSelectResult,
  });

  const tableColumns = getDepartmentsTableColumns({ onView, t });
  const tableActionsMenu = getDepartmentsTableActionsMenu({
    onView,
    onEdit,
    onDelete,
    isViewOnly,
    t,
  });

  const createButton = (
    <Button
      buttonText={t('create-department')}
      leftIcon="add"
      onClick={onCreate}
    />
  );

  const isEmptyStateVisible =
    !departmentsQueryHookResult.collection?.length && !searchQuery;
  const isEmptyStateByOutOfRangePage =
    isEmptyStateVisible && !!departmentsQueryHookResult.totalCount;

  let listContent: React.ReactNode;
  if (isEmptyStateByOutOfRangePage) {
    listContent = (
      <PageOutOfRangeEmptyState
        totalEntries={departmentsQueryHookResult.totalCount}
      />
    );
  } else if (isEmptyStateVisible) {
    listContent = (
      <ListEmptyState
        title={t('no-departments-created-yet')}
        text={t('no-departments-created-text')}
        actions={isViewOnly ? [] : [createButton]}
      />
    );
  } else {
    listContent = (
      <Table
        actionsMenu={tableActionsMenu}
        columns={tableColumns}
        counterText={t('department-plural', {
          count: departmentsQueryHookResult.totalCount,
        })}
        data={departmentsTableTree.data}
        id="departments"
        loading={departmentsQueryHookResult.loading}
        totalEntries={departmentsQueryHookResult.totalCount}
        tree={departmentsTableTree.tree}
      />
    );
  }

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      {departmentsQueryHookResult.error ? (
        <ErrorPage
          status={getStatusFromError(departmentsQueryHookResult.error)}
          description={getDescriptionFromError(
            departmentsQueryHookResult.error,
          )}
        />
      ) : (
        <>
          <PageHeader title={t('departments')}>
            {isViewOnly ? <ViewOnlyChip /> : createButton}
          </PageHeader>
          {departmentsQueryHookResult.firstLoading ? (
            <CircleLoader />
          ) : (
            <PageBody>
              {(!!departmentsQueryHookResult.collection?.length ||
                searchQuery) && (
                <Search placeholder={t('departments-search-placeholder')} />
              )}
              {listContent}
            </PageBody>
          )}
          <ConfirmationModal
            confirmButtonLabel={t('yes-delete')}
            description={<Trans i18nKey="delete-department-text" />}
            icon={IconName.Delete}
            isOpen={isModalOpened(Modals.DepartmentDestroyModal)}
            loading={destroyLoading}
            onClose={() => closeModal(Modals.DepartmentDestroyModal)}
            onConfirm={onConfirmedDelete}
            useProgress
            title={t('are-you-sure')}
          />
        </>
      )}
    </>
  );
}
